import { useState, useRef } from "react";
import { publicApi } from "@/services/api";

const INITIAL_COUNT = 8;

interface Loc {
  id: number;
  name: string;
  phone: string | null;
  whatsapp: string | null;
}

export default function LocationsGrid() {
  const [locations, setLocations] = useState<Loc[]>([]);
  const [selected, setSelected] = useState<number | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [popup, setPopup] = useState<Loc | null>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  const formatWA = (phone: string) => {
    const d = phone.replace(/[^0-9]/g, "");
    return d.startsWith("0") ? "972" + d.slice(1) : d;
  };

  useState(() => {
    publicApi
      .get("/locations")
      .then(({ data }: any) => {
        if (Array.isArray(data)) setLocations(data);
      })
      .catch(() => {});
  });

  const visibleLocations = expanded
    ? locations
    : locations.slice(0, INITIAL_COUNT);

  const handleToggle = () => {
    if (expanded && gridRef.current) {
      gridRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    setExpanded((prev) => !prev);
  };

  if (locations.length === 0) return null;

  return (
    <section className="mx-4 my-4" dir="rtl" id="locations">
      <div
        className="rounded-3xl border-[3px] border-gray-900 overflow-hidden"
        style={{ background: "#FFFFFF" }}
      >
        <div className="h-2 w-full" style={{ background: "#FDE68A" }}></div>
        <div className="p-5">
          <div className="flex items-center gap-3 mb-1">
            <div
              className="w-10 h-10 flex items-center justify-center rounded-full border-[2.5px] border-gray-900 flex-shrink-0"
              style={{ background: "#FDE68A" }}
            >
              <i className="ri-map-pin-2-fill text-gray-900 text-lg"></i>
            </div>
            <div>
              <h2 className="text-lg font-black text-gray-900 leading-tight">
                איפה אנחנו פועלים?
              </h2>
              <p className="text-xs text-gray-500 font-medium mt-0.5">
                לבחירת מוקד הקרוב לביתך
              </p>
            </div>
          </div>

          <div className="flex justify-end mb-4">
            <span
              className="text-xs font-black px-3 py-1 rounded-full border-[2px] border-gray-900"
              style={{ background: "#F0FDF4", color: "#166534" }}
            >
              {locations.length} מוקדים פעילים
            </span>
          </div>

          <div ref={gridRef} className="grid grid-cols-2 gap-2.5 mb-3">
            {visibleLocations.map((loc) => {
              const isSelected = selected === loc.id;
              return (
                <button
                  key={loc.id}
                  onClick={() => setSelected(isSelected ? null : loc.id)}
                  className="relative flex flex-col items-start gap-2 rounded-2xl border-[2px] p-3 text-right transition-all cursor-pointer"
                  style={{
                    borderColor: isSelected ? "#1a1a1a" : "#E5E7EB",
                    background: isSelected ? "#FEF9C3" : "#FAFAFA",
                    boxShadow: isSelected ? "2px 2px 0px #1a1a1a" : "none",
                  }}
                >
                  {/* Pin icon + name */}
                  <div className="flex items-start gap-1.5 w-full">
                    <span
                      className="w-5 h-5 flex items-center justify-center rounded-full flex-shrink-0 mt-0.5"
                      style={{
                        background: isSelected ? "#FDE68A" : "#F3F4F6",
                      }}
                    >
                      <i
                        className="ri-map-pin-fill text-gray-700"
                        style={{ fontSize: "10px" }}
                      ></i>
                    </span>
                    <span
                      className="text-xs font-bold text-gray-800 leading-snug text-right"
                      style={{ wordBreak: "break-word" }}
                    >
                      {loc.name}
                    </span>
                  </div>

                  {/* Contact button — shown when selected */}
                  {isSelected && (
                    <button
                      key={loc.id}
                      onClick={() => {
                        if (loc.phone || loc.whatsapp) {
                          setPopup(loc);
                        } else {
                          setSelected(isSelected ? null : loc.id);
                        }
                      }}
                      className="w-full mt-1 bg-gray-900 text-white rounded-xl py-1.5 text-xs font-black border-[1.5px] border-gray-900 hover:bg-gray-700 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      ליצירת קשר
                    </button>
                  )}

                  {/* Contact button — always visible when not selected */}
                  {!isSelected && (
                    <span
                      className="text-xs font-semibold mt-0.5"
                      style={{ color: "#6B7280" }}
                    >
                      לחץ לפרטים
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <button
            onClick={handleToggle}
            className="w-full flex items-center justify-center gap-2 rounded-2xl py-3 text-sm font-black border-[2px] border-gray-900 mb-3 transition-all cursor-pointer whitespace-nowrap"
            style={{
              background: expanded ? "#F3F4F6" : "#FEF9C3",
              color: "#1a1a1a",
            }}
          >
            <i
              className={`text-base transition-transform duration-300 ${expanded ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}`}
            ></i>
            {expanded
              ? "הצג פחות"
              : `הצג עוד מוקדים (${locations.length - INITIAL_COUNT}+)`}
          </button>
        </div>
      </div>

      {popup && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          onClick={() => setPopup(null)}
        >
          <div className="absolute inset-0 bg-black/50" />
          <div
            className="relative bg-white rounded-3xl border-[3px] border-gray-900 max-w-sm w-full p-6 text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setPopup(null)}
              className="absolute top-3 left-3 w-8 h-8 flex items-center justify-center rounded-full border-2 border-gray-200 hover:bg-gray-50 cursor-pointer"
            >
              <i className="ri-close-line text-gray-500"></i>
            </button>
            <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-yellow-100 border-2 border-gray-900 mx-auto mb-3">
              <i className="ri-map-pin-2-fill text-gray-900 text-xl"></i>
            </div>
            <h3 className="font-black text-gray-900 text-lg mb-1">
              {popup.name}
            </h3>
            <p className="text-xs text-gray-500 mb-5">ליצירת קשר עם המוקד</p>
            <div className="flex gap-3">
              {popup.whatsapp && (
                <a
                  href={`https://wa.me/${formatWA(popup.whatsapp)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl border-2 border-gray-900 text-sm font-black cursor-pointer"
                  style={{ background: "#25D366", color: "#fff" }}
                >
                  <i className="ri-whatsapp-line text-lg"></i> וואטסאפ
                </a>
              )}
              {popup.phone && (
                <a
                  href={`tel:${popup.phone}`}
                  className="flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl border-2 border-gray-900 text-sm font-black cursor-pointer bg-white text-gray-900"
                >
                  <i className="ri-phone-line text-lg"></i> שיחה טלפונית
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
